import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaExternalLinkAlt,
  FaSearch,
  FaSyncAlt,
  FaThLarge,
  FaImage,
  FaLink,
  FaSortAmountDown,
} from "react-icons/fa";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import {
  getAllProductsApp,
  deleteProductApp,
} from "./../../services/productsappServices";
import useScrollToTop from "./../../hooks/useScrollToTop";
import { ToastContainer, toast } from "react-toastify";
import AddProductAppModal from "./AddProductAppModal";
import EditProductAppModal from "./EditProductAppModal";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";

const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";

const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";

const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function truncate(str = "", max = 130) {
  if (!str) return "—";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>

          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>

          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}

        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>

        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>

      {action ? <div>{action}</div> : null}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        tones[tone] || tones.gray
      }`}
    >
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}
    >
      {children}
    </td>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        🧩
      </div>

      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucune application trouvée
      </div>

      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajoute une application ou élargis ta recherche.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3 p-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white p-3 dark:bg-slate-900">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 break-words font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

function ImageThumb({ src, alt }) {
  if (!src) {
    return (
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800">
        <FaImage />
      </div>
    );
  }

  return (
    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
      <img
        src={src}
        alt={alt || "Application"}
        className="h-full w-full object-contain"
      />
    </div>
  );
}

const ProductsAppPage = () => {
  useScrollToTop();

  const [productsApp, setProductsApp] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("order_asc");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProductId, setEditProductId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllProductsApp();
      const sortedData = Array.isArray(data)
        ? [...data].sort((a, b) => safeNumber(a.ordre_app) - safeNumber(b.ordre_app))
        : [];

      setProductsApp(sortedData);
    } catch (error) {
      console.error("Erreur lors du chargement :", error);
      toast.error("Erreur lors du chargement des applications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayedProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let rows = [...productsApp];

    if (term) {
      rows = rows.filter((item) => {
        const haystack = [
          item.nom_app,
          item.description_app,
          item.image_url_app,
          item.lien_url_app,
          item.ordre_app,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(term);
      });
    }

    rows.sort((a, b) => {
      switch (sortBy) {
        case "order_desc":
          return safeNumber(b.ordre_app) - safeNumber(a.ordre_app);

        case "name_asc":
          return String(a.nom_app || "").localeCompare(
            String(b.nom_app || ""),
            "fr"
          );

        case "name_desc":
          return String(b.nom_app || "").localeCompare(
            String(a.nom_app || ""),
            "fr"
          );

        case "order_asc":
        default:
          return safeNumber(a.ordre_app) - safeNumber(b.ordre_app);
      }
    });

    return rows;
  }, [productsApp, searchTerm, sortBy]);

  const stats = useMemo(() => {
    const withImage = displayedProducts.filter((item) => !!item.image_url_app).length;
    const withLink = displayedProducts.filter((item) => !!item.lien_url_app).length;

    const orders = displayedProducts
      .map((item) => safeNumber(item.ordre_app))
      .filter((n) => n > 0);

    const firstOrder = orders.length ? Math.min(...orders) : "—";
    const lastOrder = orders.length ? Math.max(...orders) : "—";

    return {
      total: displayedProducts.length,
      withImage,
      withLink,
      orderRange:
        firstOrder === "—" || lastOrder === "—"
          ? "—"
          : `${firstOrder} → ${lastOrder}`,
    };
  }, [displayedProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm(`Supprimer cette application ? ID: ${id}`)) {
      return;
    }

    try {
      await deleteProductApp(id);
      toast.success("Application supprimée !");
      setProductsApp((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditModal = (id) => {
    setEditProductId(id);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditProductId(null);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSortBy("order_asc");
  };

  const isEmpty = !loading && displayedProducts.length === 0;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.16),transparent_30%)]" />

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaThLarge />
                Applications & sites
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Liste des applications
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Gère les applications affichées sur ton écosystème : nom,
                description, visuel, lien public et ordre d’apparition.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter une application
              </button>

              <button
                type="button"
                onClick={fetchData}
                className={`${BUTTON_SOFT} border border-white/15 bg-white/10 text-white hover:bg-white/20`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              dark
              icon={<FaThLarge />}
              label="Applications"
              value={stats.total}
              hint="Résultats affichés"
            />

            <StatCard
              dark
              icon={<FaImage />}
              label="Images"
              value={stats.withImage}
              hint="Applications illustrées"
            />

            <StatCard
              dark
              icon={<FaLink />}
              label="Liens"
              value={stats.withLink}
              hint="Applications avec URL"
            />

            <StatCard
              dark
              icon={<FaSortAmountDown />}
              label="Ordres"
              value={stats.orderRange}
              hint="Plage d’affichage"
            />
          </div>
        </div>
      </section>

      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Retrouver rapidement une application"
          description="Recherche par nom, description, lien ou ordre d’affichage."
        />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-8">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher une application..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="xl:col-span-3">
            <select
              className={INPUT}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="order_asc">Tri : Ordre croissant</option>
              <option value="order_desc">Tri : Ordre décroissant</option>
              <option value="name_asc">Tri : Nom A → Z</option>
              <option value="name_desc">Tri : Nom Z → A</option>
            </select>
          </div>

          <div className="xl:col-span-1">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Applications enregistrées
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Liste complète des applications avec aperçu, lien et actions de
                gestion.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="purple">{displayedProducts.length} affichée(s)</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1120px]">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Application</Th>
                    <Th>Description</Th>
                    <Th>Image</Th>
                    <Th>Lien</Th>
                    <Th>Ordre</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {displayedProducts.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 transition hover:bg-slate-50/70 dark:border-slate-800 dark:hover:bg-slate-800/30"
                    >
                      <Td className="min-w-[230px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {item.nom_app}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          #{item.id}
                        </div>
                      </Td>

                      <Td className="min-w-[360px] max-w-[460px]">
                        <div className="leading-6 text-slate-600 dark:text-slate-300">
                          {truncate(item.description_app, 180)}
                        </div>
                      </Td>

                      <Td className="min-w-[110px]">
                        <ImageThumb src={item.image_url_app} alt={item.nom_app} />
                      </Td>

                      <Td className="min-w-[130px]">
                        {item.lien_url_app ? (
                          <a
                            href={item.lien_url_app}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                          >
                            <FaExternalLinkAlt />
                            Ouvrir
                          </a>
                        ) : (
                          <span className="text-sm text-slate-400">Non défini</span>
                        )}
                      </Td>

                      <Td className="min-w-[100px]">
                        <Badge tone="orange">{item.ordre_app || "—"}</Badge>
                      </Td>

                      <Td className="min-w-[120px]">
                        <div className="flex justify-end gap-2">
                          <ButtonEdit
                            onClick={() => openEditModal(item.id)}
                            hoverLabel="Modifier"
                          />
                          <ButtonDelete
                            onClick={() => deleteProduct(item.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 p-4 md:hidden">
              {displayedProducts.map((item) => (
                <div
                  key={item.id}
                  className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-800/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        {item.nom_app}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        #{item.id}
                      </div>
                    </div>

                    <Badge tone="orange">Ordre {item.ordre_app || "—"}</Badge>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <ImageThumb src={item.image_url_app} alt={item.nom_app} />

                    <div className="min-w-0 flex-1 text-sm text-slate-500 dark:text-slate-400">
                      {item.image_url_app ? "Image renseignée" : "Aucune image"}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="Description"
                      value={truncate(item.description_app, 150)}
                    />
                    <MobileInfo
                      label="Lien"
                      value={item.lien_url_app || "Non défini"}
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {item.lien_url_app ? (
                      <a
                        href={item.lien_url_app}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                      >
                        <FaExternalLinkAlt />
                        Ouvrir
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400">Pas de lien</span>
                    )}

                    <div className="ml-auto flex gap-2">
                      <ButtonEdit
                        onClick={() => openEditModal(item.id)}
                        hoverLabel="Modifier"
                      />
                      <ButtonDelete
                        onClick={() => deleteProduct(item.id)}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <AddProductAppModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        refreshList={fetchData}
      />

      {showEditModal && editProductId && (
        <EditProductAppModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          productId={editProductId}
          refreshList={fetchData}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default ProductsAppPage;